import * as React from "react";
import { useContext, useEffect, useState } from "react";
import {
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Backdrop,
  Link,
} from "@mui/material";
import {
  Menu,
  Close,
  Home,
  Savings,
  EmojiEvents,
  Person,
  PlayCircleOutline,
  Redeem,
  Wallet,
  Feed,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { authService, databaseService } from "utils/firebase";
import LocalStorage from "utils/localStorage";
// import UsaFlag from "assets/icons/usa_flag.png";
// import ColFlag from "assets/icons/col_flag.png";
import LogoFroin from "assets/logos/LogoFroinBlanco.png";
import { Store } from "hooks/main_store";
import stickyHeaderStyles from "./stickyHeaderStyles";
import Model from "hooks/Model";
import useIsMobile from "hooks/custom/useIsMobile";
import Api from "utils/api";
import "./styles.css";
import { useWallet } from "services";
import { returnNumFormated } from "utils/validations";
import DialogForm from "components/DialogForm";

interface HeaderProps {
  beforeToExit?: (callback: () => void) => () => void;
  hide?: boolean;
}

function Header({ beforeToExit, hide }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { state } = useContext(Store);
  const { pathname } = useLocation();
  const navigateUrl = useNavigate();
  const [userData, setUserData] = useState({
    username: "",
    showUsernameForm: false,
  });

  const navigate = (url: string) => {
    if (beforeToExit) {
      beforeToExit(() => navigateUrl(url))();
    } else {
      navigateUrl(url);
    }
  };

  const logOut = () => {
    Model.setData("dialog", {
      open: true,
      title: t("components.header.beforeContinue"),
      text: t("components.header.youWantLogout"),
      txtLeft: t("commons.cancel"),
      fnLeft: () => Model.setData("dialog", { open: false }),
      txtRight: t("commons.confirm"),
      fnRight: () => {
        Model.setData("dialog", {
          open: false,
        });
        Model.setData("loading", true);
        authService
          .signOut()
          .then(() => {
            Model.updateInfoUser(null);
            navigate("/");
            window.location.reload();
          })
          .catch((error) => {
            Model.updateAlerts({
              message: error?.message || error,
              variant: "error",
            });
          })
          .finally(() => Model.setData("loading", false));
      },
    });
  };

  const onChangeLanguage = () => {
    const currentLang = LocalStorage(false).getItem("defaultLanguage");
    const newLang = currentLang === "es" ? "en" : "es";
    i18n.changeLanguage(newLang, () => {
      LocalStorage(state.isIncognito).setItem("defaultLanguage", newLang);
    });
  };

  const getBalance = async () => {
    try {
      if (authService.currentUser()) {
        const userData = await databaseService.getDatabase(
          `users/${authService.currentUser().uid}`,
        );

        if (!userData.exists()) {
          await Api.post("/wallet/initBalance");
        }

        Model.setData("balance", userData.val()?.balance || 0);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    }
  };

  const updateUserName = async () => {
    try {
      Model.setData("loading", true);
      if (userData.username.length > 4 && userData.username.length < 16) {
        setUserData((prev) => ({ ...prev, showUsernameForm: false }));

        const data = await Api.post("ranking/updateCurrentUser", {
          displayName: userData.username,
        });

        if (data.ok && data.status === 200) {
          setUserData({ username: "", showUsernameForm: false });
          Model.updateInfoUser({
            ...state.infoUser,
            displayName: userData.username,
          });
          Model.updateAlerts({
            message: "Tu nombre ha sido actualizado con éxito",
            variant: "success",
          });
        } else {
          throw new Error(data.data as string);
        }
      } else {
        throw new Error("El nombre debe tener entre 5 y 15 caracteres");
      }
    } catch (error) {
      setUserData((prev) => ({ ...prev, showUsernameForm: true }));
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const firstLoad = async () => {
    try {
      Model.setData("superLoading", true);

      Model.setData("opacity", 1);
      if (authService.currentUser()?.email) {
        const rankingHistory = (
          await databaseService.getDatabase(
            `history/${authService.currentUser().uid}`,
          )
        ).val();

        Model.updateInfoUser(authService.currentUser());
        Model.setData("rankingHistory", rankingHistory);

        if (
          !authService.currentUser()?.displayName &&
          !state.infoUser?.displayName
        ) {
          setUserData((prev) => ({ ...prev, showUsernameForm: true }));
        }
      } else {
        Model.updateInfoUser(null);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("opacity", 0.9);
      getBalance();
      Model.setData("superLoading", false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  }, [pathname]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(() => {
      firstLoad();
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return hide ? (
    <></>
  ) : (
    <nav>
      {state.infoUser?.email && (
        <MobileHeader
          onChangeLanguage={onChangeLanguage}
          logOut={logOut}
          beforeToExit={beforeToExit}
        />
      )}
      <StickyHeader onChangeLanguage={onChangeLanguage} />
      <DialogForm
        show={userData.showUsernameForm}
        title={"Antes de continuar ..."}
        text={"Ingresa un nombre de usuario para identificarte:"}
        rightBtText={"Guardar"}
        funcRight={updateUserName}
        inputs={[{ label: "Nickname", value: "username", type: "text" }]}
        vc={userData}
        changeVc={(data) =>
          setUserData((prev) => ({
            ...prev,
            username: data.username.replace(/\s/g, ""),
          }))
        }
        subtext="RECUERDA: Esta acción NO se puede revertir"
      />
    </nav>
  );
}

const StickyHeader = ({ onChangeLanguage }) => {
  const styles = stickyHeaderStyles();
  const { state } = useContext(Store);

  const [isMobile, setIsMobile] = useState(false);

  useIsMobile(setIsMobile);

  return (
    <div className={styles.container}>
      <Link underline="none" href="/">
        <img alt="FROIN" src={LogoFroin} className={styles.logo} />
      </Link>
      {isMobile || true ? (
        <></>
      ) : (
        <>
          <Link underline="none" href="/profile-videos" className={styles.link}>
            <div className={styles.textLink}>Galería</div>
          </Link>
          <Link
            underline="none"
            href="/blog"
            className={styles.link}
            style={{ display: "none" }}
          >
            <div className={styles.textLink}>Blog</div>
          </Link>
          {state.infoUser?.email ? (
            <Link underline="none" href="/ranking" className={styles.link}>
              <div className={styles.textLink}>Ranking</div>
            </Link>
          ) : (
            <></>
          )}
        </>
      )}

      {!state.infoUser?.email ? (
        <div className={styles.rowLogin}>
          <LanguageButton onChangeLanguage={onChangeLanguage} />
          <button
            className={styles.loginButton}
            onClick={() => Model.setData("showAuth", true)}
            id="menu-fab-button"
          >
            <Person className={styles.loginIcon} />
            <span className={styles.loginLabel}>Ingresar</span>
          </button>
        </div>
      ) : (
        <div className={styles.blankButton} />
      )}
    </div>
  );
};

const MobileHeader = ({ onChangeLanguage, logOut, beforeToExit }) => {
  const { t } = useTranslation();
  const { state } = useContext(Store);
  const location = useLocation();
  const navigateUrl = useNavigate();

  const navigate = (url: string) => {
    if (beforeToExit) {
      beforeToExit(() => navigateUrl(url))();
    } else {
      navigateUrl(url);
    }
  };

  const [openDrawer, setOpenDrawer] = useState(false);
  const { currentBalance } = useWallet();

  const returnOptionsActions = (names = []) => {
    const options = [];

    names.forEach((name) => {
      let object = null;
      switch (name) {
        case "home":
          object = {
            icon: <Home />,
            title: "Nosotros",
            action: () => navigate("/"),
          };
          break;
        case "blog":
          object = {
            icon: <Feed />,
            title: "Blog",
            action: () => navigate("/blog"),
          };
          break;
        case "wallet":
          object = {
            icon: <Wallet />,
            title: t("components.header.wallet"),
            action: () => {
              Model.setData("dialog", {
                open: true,
                title: "Estado de Cuenta",
                text: `Tienes: ${returnNumFormated(
                  currentBalance || 0,
                )} $FROIN\n\n¿Qué deseas hacer?`,
                txtAux: t("components.header.history"),
                fnAux: () => {
                  Model.setData("dialog", { open: false });
                  navigate("/history");
                },
                txtLeft: t("components.header.receive"),
                fnLeft: () => {
                  Model.setData("dialog", { open: false });
                  navigate("/receive");
                },
                txtRight: t("components.header.send"),
                fnRight: () => {
                  Model.setData("dialog", { open: false });
                  navigate("/send");
                },
              });
            },
          };
          break;
        case "invest":
          object = {
            icon: <Savings />,
            title: t("components.header.invest"),
            action: () => {
              navigate("/welcome?page=register-investor");
              window.location.reload();
            },
          };
          break;
        case "ranking":
          object = {
            icon: <EmojiEvents />,
            title: t("components.header.ranking"),
            action: () => navigate("/ranking"),
          };
          break;
        case "profile-videos":
          object = {
            icon: <PlayCircleOutline />,
            title: "Reproduce",
            action: () => (window.location.href = "/detail-video"),
          };
          break;
        case "redeem":
          object = {
            icon: <Redeem />,
            title: "Redimir",
            action: () => navigate("/redeem"),
          };
          break;
        default:
          break;
      }

      if (object) {
        options.push(object);
      }
    });

    return options;
  };

  const returnActions = () => {
    let actions = [];
    const filterAction = (currentView: string) =>
      [
        "home",
        // "blog",
        "profile-videos",
        "ranking",
        "redeem",
        "wallet",
        // "invest",
      ].filter((view) => view !== currentView);

    if (location.pathname.includes("/send")) {
      actions = returnOptionsActions(filterAction(""));
    } else if (location.pathname.includes("/receive")) {
      actions = returnOptionsActions(filterAction(""));
    } else if (location.pathname.includes("/history")) {
      actions = returnOptionsActions(filterAction(""));
    } else if (location.pathname.includes("/ranking")) {
      actions = returnOptionsActions(filterAction("ranking"));
    } else if (
      location.pathname.includes("/profile-videos") ||
      location.pathname.includes("/detail-video")
    ) {
      actions = returnOptionsActions(filterAction("profile-videos"));
    } else if (location.pathname.includes("/redeem")) {
      actions = returnOptionsActions(filterAction("redeem"));
    } else if (location.pathname.includes("/detail-video")) {
      actions = returnOptionsActions(filterAction(""));
    } else if (location.pathname.includes("/blog")) {
      actions = returnOptionsActions(filterAction("blog"));
    } else {
      actions = returnOptionsActions(filterAction("home"));
    }

    return actions.map((item) => (
      <SpeedDialAction
        id={item.title}
        key={item.title}
        icon={item.icon}
        tooltipTitle={item.title}
        tooltipOpen
        onClick={item.action}
      />
    ));
  };

  // useEffect(() => {
  //   showTour();
  // }, []);

  return (
    <>
      <Backdrop
        open={openDrawer}
        className="backdrop-header-container"
        onClick={() => setOpenDrawer(false)}
      />
      {state.infoUser?.email ? (
        <div className="header-container" id="menu-container">
          {openDrawer ? (
            <>
              <button className="logout-btn" onClick={logOut}>
                <span className="email">
                  {state.infoUser?.displayName || state.infoUser?.email}
                </span>
                <span className="logout">{t("components.header.logOut")}</span>
              </button>
              {state.infoUser?.displayName && (
                <div className="email-float">{state.infoUser?.email}</div>
              )}
              <div className="balance-txt" style={{ display: "none" }}>
                <span className="balance-label">Tienes</span>
                <br /> {returnNumFormated(currentBalance || 0)}
                <br />
                <span className="balance-currency">$FROIN</span>
              </div>
            </>
          ) : (
            <LanguageButton onChangeLanguage={onChangeLanguage} />
          )}

          <SpeedDial
            ariaLabel="Menu"
            icon={<SpeedDialIcon icon={<Menu />} openIcon={<Close />} />}
            onClick={() => setOpenDrawer((ov) => !ov)}
            open={openDrawer}
            className="menu-container"
            direction="down"
            id="menu-fab-button"
          >
            {openDrawer ? returnActions() : null}
          </SpeedDial>
        </div>
      ) : (
        <div className="header-container">
          <SpeedDial
            ariaLabel="Menu"
            icon={<SpeedDialIcon icon={<Menu />} openIcon={<Close />} />}
            onClick={() => Model.setData("showAuth", true)}
            className="menu-container"
            direction="down"
          />
        </div>
      )}
    </>
  );
};

const LanguageButton = ({ onChangeLanguage }) => {
  // const { i18n } = useTranslation();

  return <></>;

  // return (
  //   <button className="language-btn" onClick={onChangeLanguage}>
  //     <img
  //       alt="Language"
  //       src={i18n.language === "en" ? UsaFlag : ColFlag}
  //     />
  //   </button>
  // );
};

export default Header;
